"use client"

import { RootState } from "@/(admin)/modules/auth/services/redux/store"
import { BANNERS } from "@/constants/admin-paths"
import { t } from "@/languages/i18n"
import { normalizeText } from "@/lib/external-components"
import { deleteBanner, duplicateBanner, fetchBannerId, fetchBanners } from "@/services/banner"
import { useDomainStore } from "@/stores/use-domain-store"
import { PaginationMeta } from "@/types/types"
import { getEnforcedDomainValue } from "@/utils/admin-domain-filter"
import { createPath } from "@/utils/create-path"
import { App } from "antd"
import { useRouter, useSearchParams } from "next/navigation"
import queryString from "query-string"
import { useCallback, useEffect, useRef, useState } from "react"
import { useSelector } from "react-redux"

export const useBanners = () => {
  const router = useRouter()
  const searchParams = useSearchParams()
  const { message, modal } = App.useApp()
  const account = useSelector((state: RootState) => state.account)
  const { selectedDomain } = useDomainStore()
  const enforcedDomain = getEnforcedDomainValue(selectedDomain)
  const enforcedDomainRef = useRef(enforcedDomain)

  const [dataItem, setDataItem] = useState<any[]>([])
  const [loading, setLoading] = useState(false)
  const [metaData, setMetaData] = useState<PaginationMeta>()
  const [totalItem, setTotalItem] = useState(0)
  const [bannerNameOptions, setBannerNameOptions] = useState<{ value: string | number; label: string }[]>([])
  const [filterTabBanner, setFilterTabBanner] = useState("all")
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([])
  const [bulkAction, setBulkAction] = useState<string>("")
  const [bannerItemData, setBannerItemData] = useState<any>()
  const [needRefresh, setNeedRefresh] = useState<boolean>(false)
  const [pagination, setPagination] = useState<{
    page: number
    pageSize: number
    total: number
    pageCount: number
  }>({
    page: 1,
    pageSize: 20,
    total: 0,
    pageCount: 0,
  })

  useEffect(() => {
    enforcedDomainRef.current = enforcedDomain
  }, [enforcedDomain])

  const fetchBannersData = useCallback(
    async (searchTerms?: { [key: string]: string }) => {
      setLoading(true)
      setDataItem([])
      try {
        const params = {
          page: String(pagination.page),
          pageSize: String(pagination.pageSize),
          ...searchTerms,
          ...(enforcedDomainRef.current ? { domain: enforcedDomainRef.current } : {}),
        }
        const allResponse = await fetchBanners(params)
        const response = allResponse && allResponse.data
        const meta = allResponse?.meta
          ? {
            pagination: {
              total: allResponse.meta.pagination?.total,
              page: allResponse.meta.pagination?.page,
              pageSize: allResponse.meta.pagination?.pageSize,
              pageCount: allResponse.meta.pagination?.pageCount,
            },
          }
          : undefined
        setMetaData(meta)

        if (response && response.length > 0) {
          setDataItem(response)
          if (searchTerms?.publicationState === "all") {
            setTotalItem(meta?.pagination?.total || 0)
          }
          setBannerNameOptions(
            response.map((banner: { id: number; attributes: any }) => ({
              value: banner.id,
              label: banner.attributes.name,
            })),
          )
        }

        if (meta?.pagination) {
          setPagination({
            page: meta.pagination.page || 1,
            pageSize: meta.pagination.pageSize || 20,
            total: meta.pagination.total || 0,
            pageCount: meta.pagination.pageCount || 0,
          })
        }

        return response
      } catch (error) {
        message.error(t("Lỗi khi tải danh sách banner. Vui lòng thử lại."))
      } finally {
        setLoading(false)
      }
    },
    [message, pagination.page, pagination.pageSize],
  )

  const getBannerInformation = useCallback(
    async (id: number) => {
      try {
        const res = await fetchBannerId(id)
        setBannerItemData(res)
      } catch (error) {
        message.error(t("Lỗi khi tải thông tin banner!"))
      }
    },
    [message],
  )

  const handleDuplicate = useCallback(
    async (bannerId: number, filterValues?: Record<string, string>, sortPaginationValues?: Record<string, string>) => {
      try {
        setLoading(true)
        await duplicateBanner(bannerId)
        message.success(t("Nhân bản banner thành công!"))
        const currentParams = {
          locale: "all",
          ...filterValues,
          ...sortPaginationValues,
          publicationState: filterTabBanner,
        }
        fetchBannersData(currentParams)
      } catch (error) {
        message.error(t("Lỗi khi nhân bản banner. Vui lòng thử lại."))
      } finally {
        setLoading(false)
      }
    },
    [message, fetchBannersData, filterTabBanner],
  )

  const handleDelete = useCallback(
    async (id: number, filterValues?: Record<string, string>, sortPaginationValues?: Record<string, string>) => {
      modal.confirm({
        title: t("Xác nhận xóa"),
        content: t("Bạn có chắc chắn muốn xóa banner này?"),
        okText: t("Xóa"),
        cancelText: t("Hủy"),
        onOk: async () => {
          setLoading(true)
          try {
            await deleteBanner(id)
            const currentParams = {
              locale: "all",
              ...filterValues,
              ...sortPaginationValues,
              publicationState: filterTabBanner,
            }
            fetchBannersData(currentParams)
            message.success(t("Xoá banner thành công!"))
          } catch {
            message.error(t("Xóa banner thất bại!"))
          } finally {
            setLoading(false)
          }
        },
      })
    },
    [modal, message, filterTabBanner, fetchBannersData],
  )

  const handleBulkDelete = useCallback(
    async (filterValues?: Record<string, string>, sortPaginationValues?: Record<string, string>) => {
      setLoading(true)
      try {
        const ids = selectedRowKeys.map(id => Number(id))
        await Promise.all(ids.map(id => deleteBanner(id)))
        setSelectedRowKeys([])
        const currentParams = {
          locale: "all",
          ...filterValues,
          ...sortPaginationValues,
          publicationState: filterTabBanner,
        }
        fetchBannersData(currentParams)
        message.success(t("Xoá banner thành công!"))
      } catch {
        message.error(t("Lỗi khi thực hiện xóa hàng loạt"))
      } finally {
        setLoading(false)
      }
    },
    [selectedRowKeys, message, filterTabBanner, fetchBannersData],
  )

  const handleApplyBulkAction = useCallback(
    (filterValues?: Record<string, string>, sortPaginationValues?: Record<string, string>) => {
      if (bulkAction === "delete" && selectedRowKeys.length > 0) {
        modal.confirm({
          title: t("Xác nhận xóa"),
          content: t("Bạn có chắc chắn muốn xóa các banner đã chọn không?"),
          okText: t("Xóa"),
          cancelText: t("Hủy"),
          onOk: () => handleBulkDelete(filterValues, sortPaginationValues),
        })
      } else if (selectedRowKeys.length === 0) {
        message.warning(t("Vui lòng chọn các banner để thực hiện thao tác."))
      } else {
        message.warning(t("Vui lòng chọn một thao tác hàng loạt hợp lệ."))
      }
    },
    [bulkAction, selectedRowKeys, modal, message, handleBulkDelete],
  )

  const handleEdit = useCallback(
    (id: number) => {
      router.push(createPath(BANNERS.EDIT.PATH, searchParams, { id }))
    },
    [router],
  )

  const handleRefresh = useCallback(
    (filterRef?: { current: { filterFunction: () => void } | null }) => {
      if (filterRef?.current) {
        filterRef.current.filterFunction()
      }
      setNeedRefresh(!needRefresh)
    },
    [needRefresh],
  )

  const handleSearch = useCallback(
    (filters: object, pathname: string) => {
      const values: { [key: string]: string } = {}
      Object.keys(filters).forEach(key => {
        const value = filters[key as keyof typeof filters]
        if (typeof value === "string") {
          values[key] = value
        }
      })

      if (!values) {
        message.error("Giá trị tìm kiếm không hợp lệ")
        return
      }

      let newValue = { ...values }

      if (values.search) {
        newValue = { ...newValue, search: normalizeText(values.search) }
      }

      if (enforcedDomain) {
        newValue = { ...newValue, domain: enforcedDomain }
      }

      const searchParams = queryString.stringify(newValue, { encode: false })
      const newUrl = searchParams.length > 0 ? `${pathname}?${searchParams}` : pathname
      router.push(`${newUrl}`)
    },
    [router, message, enforcedDomain],
  )

  return {
    dataItem,
    loading,
    metaData,
    totalItem,
    bannerNameOptions,
    filterTabBanner,
    selectedRowKeys,
    bulkAction,
    bannerItemData,
    needRefresh,
    pagination,

    actions: {
      fetchBannersData,
      getBannerInformation,
      handleDelete,
      handleBulkDelete,
      handleApplyBulkAction,
      handleEdit,
      handleDuplicate,
      handleRefresh,
      handleSearch,
      setSelectedRowKeys,
      setBulkAction,
      setFilterTabBanner,
      setPagination,
    },

    setters: {
      setDataItem,
      setLoading,
      setMetaData,
      setTotalItem,
      setBannerNameOptions,
      setSelectedRowKeys,
      setBulkAction,
      setBannerItemData,
      setNeedRefresh,
      setPagination,
    },
  }
}
